import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAgentCapabilities } from '../lib/agent-capabilities-api';
import {
  DEFAULT_AGENT_CAPABILITIES_PREFERENCE,
  readAgentCapabilitiesPreference,
  rememberAgentCapabilities,
  resolveAgentCapabilityControls,
  setAgentCapability,
  subscribeAgentCapabilitiesPreference,
  type AgentCapabilitiesPreference,
  type AgentCapabilityKey,
} from '../lib/agent-capabilities-preference';
import { QUERY_KEYS } from '../lib/query-keys';
import { useWorkspace } from '../providers/useWorkspace';
import { useSubscriptionState } from '../providers/useSubscriptionState';

/**
 * What the composer controls may offer this user. Availability is a server
 * concern (flags, canary allowlist, provider keys, plan), so the answer is
 * never computed client side. Employer workspaces never ask: the server
 * ignores the controls on employer threads.
 */
export function useAgentCapabilitiesQuery() {
  const { mode, activeWorkspaceId } = useWorkspace();
  const { snapshot } = useSubscriptionState();

  const query = useQuery({
    queryKey: [
      ...QUERY_KEYS.AGENT_CAPABILITIES,
      activeWorkspaceId,
      snapshot?.tier,
    ],
    queryFn: () => fetchAgentCapabilities('seeker'),
    enabled: mode === 'seeker',
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // The chat runtime reads availability at send time, outside React.
  useEffect(() => {
    if (query.data) rememberAgentCapabilities(query.data);
  }, [query.data]);

  return query;
}

/** The sticky preference, kept in sync across every mounted composer. */
export function useAgentCapabilitiesPreference() {
  const preference = useSyncExternalStore(
    subscribeAgentCapabilitiesPreference,
    readAgentCapabilitiesPreference,
    () => DEFAULT_AGENT_CAPABILITIES_PREFERENCE,
  );

  const setCapability = useCallback(
    <K extends AgentCapabilityKey>(key: K, value: AgentCapabilitiesPreference[K]) => {
      setAgentCapability(key, value);
    },
    [],
  );

  return { preference, setCapability };
}

/**
 * Preference plus availability, reconciled into the controls the composer
 * draws. Both the web chips and the "+" sheet rows read this one hook.
 */
export function useAgentCapabilities() {
  const { mode } = useWorkspace();
  const query = useAgentCapabilitiesQuery();
  const { preference, setCapability } = useAgentCapabilitiesPreference();
  const capabilities = mode === 'seeker' ? query.data ?? null : null;

  const controls = useMemo(
    () => resolveAgentCapabilityControls(preference, capabilities),
    [capabilities, preference],
  );

  return { preference, capabilities, controls, setCapability };
}
